import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { TID } from '@/constants/testIds/voice';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import FloatingDialer from '@/components/FloatingDialer';
import {
    Gauge, PhoneList, Phone, Stack, Tree, Microphone, UsersThree,
    Receipt, EnvelopeSimple, Gear, Wrench, ShieldWarning, ListChecks,
    BookOpen, SignOut, WaveSine, Broadcast, Queue, VideoCamera,
    ArrowsLeftRight, ArrowsClockwise, Clock, AddressBook, Key, DeviceMobile, Sun,
    PhoneCall, ChartBar, ShieldCheck, SpeakerHigh, Television, LockKey, CloudArrowUp, Pulse,
} from '@phosphor-icons/react';

const NAV = [
    { to: '/', label: 'Dashboard', icon: Gauge, tid: TID.navDashboard },
    { to: '/ops', label: 'Ops Center · Live', icon: Pulse, tid: 'nav-ops' },
    { to: '/intercom', label: 'Interkom (interní)', icon: Broadcast, tid: 'nav-intercom' },
    { to: '/softphone', label: 'Softphone (SIP)', icon: PhoneCall, tid: 'nav-softphone' },
    { to: '/sip-connect', label: 'Připojení telefonu (návod)', icon: DeviceMobile, tid: 'nav-sip-connect' },
    { to: '/twilio-phone', label: 'Twilio Telefon (PSTN)', icon: Phone, tid: 'nav-twilio-phone' },
    { to: '/monitor', label: 'Live Monitor', icon: WaveSine, tid: 'nav-monitor' },
    { to: '/cdr', label: 'CDR Reports', icon: ChartBar, tid: 'nav-cdr' },
    { to: '/wallboard', label: 'Wallboard', icon: Television, tid: 'nav-wallboard' },
    { divider: true, label: 'Telephony' },
    { to: '/dids', label: 'DID čísla', icon: PhoneList, tid: TID.navDids },
    { to: '/extensions', label: 'SIP Účty', icon: Phone, tid: TID.navExtensions },
    { to: '/trunks', label: 'SIP Trunky', icon: Stack, tid: TID.navTrunks },
    { to: '/queues', label: 'Call Queues', icon: Queue, tid: 'nav-queues' },
    { to: '/ring-groups', label: 'Ring Groups', icon: UsersThree, tid: TID.navRingGroups },
    { to: '/conferences', label: 'Konference', icon: VideoCamera, tid: 'nav-conferences' },
    { divider: true, label: 'Routing' },
    { to: '/inbound-routes', label: 'Inbound Routes', icon: ArrowsLeftRight, tid: 'nav-inbound' },
    { to: '/outbound-routes', label: 'Outbound Routes', icon: ArrowsClockwise, tid: 'nav-outbound' },
    { to: '/time-conditions', label: 'Time Conditions', icon: Clock, tid: 'nav-tc' },
    { to: '/call-flows', label: 'Call Flow', icon: Tree, tid: TID.navCallFlows },
    { to: '/ivr', label: 'IVR Builder (Hlavní)', icon: Tree, tid: 'nav-ivr-menu' },
    { to: '/ivr-audio', label: 'TTS / Audio knihovna', icon: Microphone, tid: TID.navIvr },
    { divider: true, label: 'Monitoring' },
    { to: '/call-logs', label: 'Call Logy', icon: Receipt, tid: TID.navCallLogs },
    { to: '/recordings', label: 'Nahrávky', icon: SpeakerHigh, tid: 'nav-recordings' },
    { to: '/voicemail', label: 'Voicemail', icon: EnvelopeSimple, tid: TID.navVoicemail },
    { to: '/contacts', label: 'Phonebook', icon: AddressBook, tid: 'nav-contacts' },
    { divider: true, label: 'Systém' },
    { to: '/settings', label: 'DIDWW', icon: Gear, tid: TID.navSettings },
    { to: '/fayn', label: 'Fayn / Yeastar', icon: Broadcast, tid: 'nav-fayn' },
    { to: '/solar-green', label: 'Solar Green CMS', icon: Sun, tid: 'nav-solar-green' },
    { to: '/pbx', label: 'PBX Konfigurace', icon: Wrench, tid: TID.navPbx },
    { to: '/provisioning', label: 'IP Phone Provisioning', icon: DeviceMobile, tid: 'nav-provisioning' },
    { to: '/hot-desking', label: 'Hot Desking', icon: CloudArrowUp, tid: 'nav-hot-desking' },
    { to: '/twilio', label: 'Twilio Voice', icon: PhoneCall, tid: 'nav-twilio' },
    { to: '/twilio-flow', label: 'Twilio Call Flow', icon: Tree, tid: 'nav-twilio-flow' },
    { to: '/api-keys', label: 'API Keys', icon: Key, tid: 'nav-api-keys' },
    { to: '/security', label: 'Zabezpečení', icon: ShieldCheck, tid: 'nav-security' },
    { to: '/2fa', label: 'Dvoufaktorové ověření', icon: LockKey, tid: 'nav-2fa' },
    { to: '/toll-fraud', label: 'Toll Fraud', icon: ShieldWarning, tid: TID.navTollFraud },
    { to: '/audit', label: 'Audit Log', icon: ListChecks, tid: TID.navAudit },
    { to: '/users', label: 'Uživatelé', icon: UsersThree, tid: TID.navUsers },
    { to: '/docs', label: 'Dokumentace', icon: BookOpen, tid: TID.navDocs },
];

const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="flex min-h-screen" style={{ background: 'var(--bg-app)' }}>
            <aside
                data-testid={TID.sidebar}
                className="w-64 shrink-0 border-r flex flex-col"
                style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}
            >
                <div className="px-5 py-5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="Solar Green" className="h-9" />
                        <div>
                            <div className="font-heading text-xs font-bold tracking-[0.15em] uppercase">
                                VOIP Gateway
                            </div>
                            <div className="text-[9px] uppercase tracking-[0.2em]"
                                 style={{ color: 'var(--text-disabled)' }}>
                                Private System
                            </div>
                        </div>
                    </div>
                </div>

                <nav data-testid={TID.sidebarNav} className="flex-1 overflow-y-auto py-3">
                    {NAV.map((item, idx) => {
                        if (item.divider) {
                            return (
                                <div key={`d${idx}`}
                                     className="px-5 mt-4 mb-2 text-[10px] font-bold uppercase tracking-[0.15em]"
                                     style={{ color: 'var(--text-disabled)' }}>
                                    {item.label}
                                </div>
                            );
                        }
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/'}
                                data-testid={item.tid}
                                className={({ isActive }) =>
                                    `nav-link flex items-center gap-3 px-5 py-2 text-sm ${
                                        isActive ? 'active text-white' : 'text-[var(--text-secondary)] hover:text-white'
                                    }`
                                }
                            >
                                <Icon size={18} weight="regular" />
                                <span>{item.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>

                <PWAInstallPrompt />

                <div className="border-t px-5 py-4" style={{ borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center justify-between">
                        <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{user?.name || '—'}</div>
                            <div className="text-[11px] truncate font-mono"
                                 style={{ color: 'var(--text-secondary)' }}>
                                {user?.email}
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.1em] font-bold"
                                 style={{ color: 'var(--brand)' }}>
                                {(user?.roles || []).join(' · ')}
                            </div>
                        </div>
                        <button
                            data-testid={TID.logoutBtn}
                            onClick={handleLogout}
                            className="p-2 hover:bg-[var(--bg-surface-hover)]"
                            title="Odhlásit"
                        >
                            <SignOut size={18} />
                        </button>
                    </div>
                </div>
            </aside>

            <main className="flex-1 min-w-0 overflow-x-hidden">
                <div className="max-w-[1600px] mx-auto px-8 py-6">{children}</div>
            </main>

            <FloatingDialer />
        </div>
    );
};

export default Layout;
